import { useSearchBlockedUsersQuery } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  DataGrid,
  type GridEventListener,
  type GridRowParams,
  type GridSortModel,
  NoRowsOverlay,
} from '@shared/components';
import { useMemo } from 'react';

import { useAdminBlockedUsersListSearchParams } from '../../hooks';
import { EmptyBlockedUsersOverlay } from '../empty-blocked-users-overlay';

import { columns } from './columns';
import { mapBlockedUsersListData } from './mapper';

export default function BlockedUsersList() {
  const [
    { page, pageSize, sortBy, sortOrder, search, searchBlockedUsersInput },
    { changePageNumber, changePageSize, changeSortingField },
  ] = useAdminBlockedUsersListSearchParams();
  const navigate = useNavigate();

  const { data: blockedUsersData, loading: isSearchingBlockedUsers } = useSearchBlockedUsersQuery({
    variables: searchBlockedUsersInput,
  });

  const { blockedUsers, total } = useMemo(() => {
    const searchResults = blockedUsersData?.ecSearchBlockedUsers;

    if (!searchResults) {
      return {
        blockedUsers: [],
        total: 0,
      };
    }

    return {
      blockedUsers: searchResults.blockedUsers.map(mapBlockedUsersListData),
      total: searchResults.total ?? 0,
    };
  }, [blockedUsersData]);

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'firstName';
    const sort = sorting[0]?.sort ?? 'asc';
    changeSortingField(field, sort);
  };

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<(typeof blockedUsers)[number]>,
  ) => {
    navigate('/admin/blocklist/:blockedUserId', {
      params: {
        blockedUserId: params.row.id,
      },
    });
  };

  const isSearching = search !== '';

  return (
    <DataGrid
      columns={columns}
      rows={blockedUsers}
      loading={isSearchingBlockedUsers}
      hideFooter={total === 0}
      rowHeight={48}
      initialState={{
        sorting: {
          sortModel: [{ field: sortBy, sort: sortOrder }],
        },
      }}
      onSortModelChange={handleSortModelChange}
      onRowClick={handleRowClick}
      disableColumnMenu
      disableRowSelectionOnClick
      slots={{
        noRowsOverlay: () => {
          if (isSearching) {
            return <NoRowsOverlay />;
          }

          return <EmptyBlockedUsersOverlay />;
        },
      }}
      slotProps={{
        footer: {
          total: total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              changePageNumber(0);
              return changePageSize(pageSize);
            },
          },
        },
      }}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.background.paper,
        '.MuiDataGrid-cell': {
          p: theme.spacing(3),
        },
        '.MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '.MuiDataGrid-columnHeader': {
          px: theme.spacing(3),
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '--DataGrid-overlayHeight': '404px',
      })}
    />
  );
}
